import { useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { ClientLogosSlider } from './ui/ClientLogosSlider';
import { InfiniteSlider } from './ui/infinite-slider';
import { Quote } from 'lucide-react';
import sarahJohnsonImage from '@/assets/sarah-johnson-testimonial.jpg';
import michaelHarrisImage from '@/assets/michael-harris-testimonial.jpg';
import emmaRodriguezImage from '@/assets/emma-rodriguez-testimonial.jpg';
import priyaKapoorImage from '@/assets/testimonials/priya-kapoor.jpg';
import alexThompsonImage from '@/assets/testimonials/alex-thompson.jpg';
import davidParkImage from '@/assets/testimonials/david-park.jpg';
import elenaRodriguezImage from '@/assets/testimonials/elena-rodriguez.jpg';
import marcusWilliamsImage from '@/assets/testimonials/marcus-williams.jpg';

const ClientsSection = () => {
  const {
    getTransform
  } = useScrollAnimation({
    intensity: 0.03
  });

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('show');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => observer.observe(el));
    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const testimonials = [
    {
      quote: "gimmefy transformed our content creation process. What used to take weeks now takes days, and the quality is consistently excellent.",
      author: "Sarah Johnson",
      position: "Marketing Director",
      image: sarahJohnsonImage
    },
    {
      quote: "The AI understands our brand voice perfectly. It's like having a seasoned copywriter who never sleeps.",
      author: "Michael Harris",
      position: "Creative Director",
      image: michaelHarrisImage
    },
    {
      quote: "ROI increased 40% in the first quarter. gimmefy doesn't just create content—it creates results.",
      author: "Emma Rodriguez",
      position: "CMO",
      image: emmaRodriguezImage
    },
    {
      quote: "We went from 3 blog posts a month to 15—without adding headcount. gimmefy is our secret weapon for scaling content.",
      author: "Priya Kapoor",
      position: "Head of Content",
      image: priyaKapoorImage
    },
    {
      quote: "Finally, an AI tool that actually gets our brand voice. My team stopped rewriting everything from scratch.",
      author: "Alex Thompson",
      position: "Brand Manager",
      image: alexThompsonImage
    },
    {
      quote: "Our social calendar used to be a nightmare. Now it practically runs itself. I actually have time to think strategically.",
      author: "David Park",
      position: "Growth Lead",
      image: davidParkImage
    },
    {
      quote: "The Playbooks feature alone saved us 20 hours a week. It's like having templates that actually work.",
      author: "Elena Rodriguez",
      position: "Digital Marketing Manager",
      image: elenaRodriguezImage
    },
    {
      quote: "I was skeptical about AI content, but gimmefy proved me wrong. The quality rivals our best writers.",
      author: "Marcus Williams",
      position: "VP of Marketing",
      image: marcusWilliamsImage
    }
  ];

  return (
    <section id="clients-section-container" className="py-24 bg-background relative overflow-hidden">
      {/* Decorative shapes with scroll-based movement */}
      <div className="absolute w-24 h-24 bg-primary/20 rounded-full top-[10%] left-[5%] shape-animation" style={{
        transform: `translate(${getTransform(0, 0.8)}px, ${getTransform(0, 0.9)}px) rotate(${getTransform(0, 0.5)}deg)`
      }}></div>
      <div className="absolute w-32 h-32 bg-accent/30 bottom-[20%] right-[5%] triangle-shape shape-animation" style={{
        animationDelay: '2s',
        transform: `translate(${getTransform(0, -0.7)}px, ${getTransform(0, -0.8)}px) rotate(${getTransform(0, -0.6)}deg)`
      }}></div>
      <div className="absolute w-16 h-16 bg-secondary/40 top-[35%] right-[15%] rounded-full shape-animation" style={{
        animationDelay: '3.5s',
        transform: `translate(${getTransform(0, -0.9)}px, ${getTransform(0, 0.6)}px) rotate(${getTransform(0, 0.8)}deg)`
      }}></div>

      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 animate-on-scroll">
          <h2 id="clients-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">Loved by <span className="bg-gradient-primary bg-clip-text text-transparent">innovators</span> everywhere</h2>
          <p id="clients-subtitle" className="text-xl text-muted-foreground">
            Join thousands of marketers who've accelerated their creative workflow with gimmefy.
          </p>
        </div>

        <div id="clients-logos" className="animate-on-scroll">
          <ClientLogosSlider />
        </div>

        {/* Testimonial infinite slider */}
        <div id="clients-testimonials" className="mt-16 animate-on-scroll">
          <InfiniteSlider
            duration={40}
            durationOnHover={80}
            gap={24}
          >
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="w-[500px] lg:w-[600px] flex-shrink-0 bg-card/80 backdrop-blur-sm border border-border/50 shadow-elegant rounded-2xl"
              >
                <CardContent className="p-6 lg:p-8">
                  <div className="grid gap-4 lg:gap-6 grid-cols-[auto_1fr] items-center">
                    {/* Author Image */}
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-primary rounded-xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                      <div className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 rounded-xl overflow-hidden border-2 border-white/20 shadow-lg">
                        <img
                          src={testimonial.image}
                          alt={`${testimonial.author}, ${testimonial.position}`}
                          className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </div>

                    {/* Testimonial Content */}
                    <div className="space-y-3">
                      <div className="relative">
                        <Quote className="h-6 w-6 text-primary/30 absolute -top-1 -left-1" />
                        <blockquote className="text-sm lg:text-base font-medium text-foreground leading-relaxed pl-5">
                          "{testimonial.quote}"
                        </blockquote>
                      </div>
                      
                      <div className="space-y-0.5 pl-5">
                        <div className="text-sm lg:text-base font-bold text-foreground">
                          {testimonial.author}
                        </div>
                        <div className="text-xs lg:text-sm text-primary font-medium">
                          {testimonial.position}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </InfiniteSlider>
        </div>
      </div>
    </section>
  );
};

export default ClientsSection;
